/**
 * Helpers for list ↔ detail navigation that preserve pagination in the URL.
 */

export function buildReturnTo(location) {
  return `${location.pathname}${location.search || ''}`;
}

export function listScrollKey(pathname) {
  return `listScroll:${pathname}`;
}

export function consumeListScrollId(storageKey) {
  try {
    const id = sessionStorage.getItem(storageKey);
    if (id) {
      sessionStorage.removeItem(storageKey);
      return id;
    }
  } catch (e) {
    // ignore storage errors
  }
  return null;
}

export function resolveListScrollId(location) {
  const fromState = location.state?.scrollJobId;
  if (fromState) return fromState;
  return consumeListScrollId(listScrollKey(location.pathname));
}

export function clearScrollJobIdFromState(navigate, location) {
  if (!location.state?.scrollJobId) return;
  const { scrollJobId: _removed, ...rest } = location.state || {};
  navigate(`${location.pathname}${location.search}`, { replace: true, state: rest });
}

/**
 * Merge query updates into the current list URL.
 * - Setting a value to null/undefined/'' deletes the key.
 * - page=1 and tab=ongoing are omitted (defaults).
 */
export function updateListQuery(navigate, location, updates = {}, { replace = true } = {}) {
  const p = new URLSearchParams(location.search);
  Object.entries(updates).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      p.delete(key);
      return;
    }
    if (key === 'page' && (Number(value) === 1 || String(value) === '1')) {
      p.delete('page');
      return;
    }
    if (key === 'tab' && String(value) === 'ongoing') {
      p.delete('tab');
      return;
    }
    if (key === 'technician' && String(value) === 'all') {
      p.delete('technician');
      return;
    }
    p.set(key, String(value));
  });
  const qs = p.toString();
  navigate(qs ? `${location.pathname}?${qs}` : location.pathname, {
    replace,
    state: location.state,
  });
}

export function parsePageFromSearch(search, fallback = 1) {
  const p = new URLSearchParams(typeof search === 'string' ? search : '');
  const n = Number(p.get('page'));
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : fallback;
}

export function navigateBack(navigate, location, fallback = '/jobs') {
  const scrollJobId = location?.state?.scrollJobId;
  const returnTo = location?.state?.returnTo;

  if (window.history.length > 1) {
    navigate(-1);
    return;
  }

  if (returnTo) {
    navigate(returnTo, {
      replace: true,
      state: scrollJobId ? { scrollJobId } : undefined,
    });
    return;
  }

  navigate(fallback);
}

export function navigateToDetail(navigate, location, path, scrollIdKey, scrollId) {
  const storageKey = listScrollKey(location.pathname);
  try {
    sessionStorage.setItem(storageKey, String(scrollId));
  } catch (e) {
    // ignore storage errors
  }
  navigate(path, {
    state: {
      returnTo: buildReturnTo(location),
      [scrollIdKey]: scrollId,
    },
  });
}
